import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  z,
} from 'react';
import { View, Flex } from '@smarterservices/smarter-ui';
import {
  getStore,
  useVideoFragmentStore,
  usePlayerStore,
  useLayoutStore,
} from '../../../../stores/StoreContext';
import PropTypes from 'prop-types';
import HlsViewer from '../../../Common/HlsViewer';
import {config} from '../../../../config';
import { randomId } from '../../../../helpers/utilities';
import ProgressBar from './ProgressBar';

// This component uses CSS positioning to show specific regions of the HLS video stream
// without using canvas drawing by sharing the MediaStream from the main video element
const HlsFragment = forwardRef(
  (
    {
      SessionTrack,
      isFocusedTrack = false,
      width = null,
      height = null,
      style = {},
      startTime = 0,
      endTime = 0,
      leadInTime = 0,
      leadOutTime = 0,
      displayProgressBar = true,
      autoplay = false,
      onTimeUpdate,
      onPlay,
      onPause,
      onEnded,
      onSeeking,
      onSeeked,
      onLoadStart,
      onLoadedMetadata,
      onLoadedData,
      onResize,
      onError,
      onReady,
      ...props
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const hlsViewerRef = useRef(null);

    const ExamSessionStore = getStore('examSessionStore');
    const VideoFragmentStore = getStore('videoFragmentStore');
    const [fragmentId] = useState(randomId());
    const progressPercentage = useVideoFragmentStore(
      (state) => state[fragmentId]?.progressPercentage
    );
    const duration = useVideoFragmentStore(
      (state) => state[fragmentId]?.duration
    );
    const currentTime = useVideoFragmentStore(
      (state) => state[fragmentId]?.currentTime
    );

    //const progressPercentage = 0;
    //const duration = 0;
    //const currentTime = 0;
    useEffect(() => {
      // On unmount, delete the fragment
      return () => {
        VideoFragmentStore.deleteFragment(fragmentId);
      };
    }, [fragmentId]);

    // Track if onReady has been called to prevent multiple invocations
    const onReadyCalledRef = useRef(false);

    const handleOnReady = (hlsViewerData) => {
      // Only proceed if this is the first call
      if (onReadyCalledRef.current) {
        return;
      }

      // Mark as called
      onReadyCalledRef.current = true;

      // Create the fragment in the store
      VideoFragmentStore.createFragment(
        fragmentId,
        SessionTrack,
        hlsViewerRef,
        { startTime, endTime, leadInTime, leadOutTime }
      );

      // Call onReady with the fragmentId
      if (onReady && typeof onReady === 'function') {
        onReady(fragmentId);
      }
    };

    // Expose HlsViewer methods to parent component
    useImperativeHandle(ref, () => ({
      // Expose the fragmentId to the parent component
      fragmentId,

      // Add a method to set the current time directly
      setTime: (time) => {
        if (hlsViewerRef.current) {
          hlsViewerRef.current.currentTime = time;
        }
      },
      play: () => {
        if (hlsViewerRef.current) {
          VideoFragmentStore.play(fragmentId);
        }
      },
      pause: () => {
        if (hlsViewerRef.current) {
          VideoFragmentStore.pause(fragmentId);
        }
      },
      restart: () => {
        if (hlsViewerRef.current) {
          VideoFragmentStore.play(fragmentId, 0);
        }
      },
      stepForward: () => {
        if (hlsViewerRef.current) {
          VideoFragmentStore.stepForward(fragmentId);
        }
      },
      stepBackward: () => {
        if (hlsViewerRef.current) {
          VideoFragmentStore.stepBackward(fragmentId);
        }
      },
      seek: (timeInSeconds) => {
        if (hlsViewerRef.current) {
          VideoFragmentStore.play(fragmentId, timeInSeconds);
        }
      },
      getCurrentTime: () => {
        if (hlsViewerRef.current) {
          return VideoFragmentStore.getFragment(fragmentId).state.currentTime;
        }
        return 0;
      },
      getDuration: () => {
        if (hlsViewerRef.current) {
          return VideoFragmentStore.getFragment(fragmentId).state.duration;
        }
        return 0;
      },
      isPlaying: () => {
        if (hlsViewerRef.current) {
          return VideoFragmentStore.getFragment(fragmentId).state.isPlaying;
        }
        return false;
      },
    }));

    const [viewPortSize, setViewPortSize] = useState({
      width: 500,
      height: 500,
    });
    const [resizeBasedOn, setResizeBasedOn] = useState('');
    const [containerStyle, setContainerStyle] = useState({});
    const [videoStyle, setVideoStyle] = useState({});
    const [maxVideoSize, setMaxVideoSize] = useState({ width: 0, height: 0 });
    const [parentSize, setParentSize] = useState({ width: 0, height: 0 });
    const [videoHeight, setVideoHeight] = useState(100);
    const [positioning, setPositioning] = useState({
      left: 0,
      top: 0,
      width: 640,
      height: 460,
      sessionChangeTime: 0,
      id: 'default',
    });

    const tracks = usePlayerStore((state) => state.tracks);
    const videoContainerSize = useLayoutStore(
      (state) => state.containers.video.size
    );
    const controlsContainerSize = useLayoutStore(
      (state) => state.containers.controls.size
    );

    const hlsUrl = `${config.media.playback.baseUrl}/v1/${ExamSessionStore.getHlsAuthenticationToken()}/installs/${ExamSessionStore.installSid}/exams/${ExamSessionStore.examSid}/sessions/${ExamSessionStore.sessionSid}/playlist.m3u8`;

    // Get the current positioning based on the current time
    const getCurrentPositioning = () => {
      if (
        !SessionTrack?.hls?.track?.positions ||
        SessionTrack.hls.track.positions.length === 0
      ) {
        // Return default positioning if no positions are available
        return {
          left: 0,
          top: 0,
          width: 640,
          height: 360,
          sessionChangeTime: 0,
          id: 'default',
        };
      }

      // Sort positions by sessionChangeTime
      const sortedPositions = [...SessionTrack.hls.track.positions].sort(
        (a, b) => a.sessionChangeTime - b.sessionChangeTime
      );

      // Find the position that applies to the current time
      let currentPosition = sortedPositions[0]; // Default to first position

      for (let i = 0; i < sortedPositions.length; i++) {
        if (sortedPositions[i].sessionChangeTime <= currentTime) {
          currentPosition = sortedPositions[i];
        } else {
          break; // Stop once we find a position with change time greater than current time
        }
      }

      return currentPosition;
    };

    // Update positioning when currentTime changes
    useEffect(() => {
      if (SessionTrack?.hls?.track?.positions?.length > 0) {
        const newPosition = getCurrentPositioning();
        newPosition.aspectRatio = newPosition.height / newPosition.width;
        setPositioning(newPosition);
      }
    }, [currentTime, SessionTrack]);

    // Calculate the video style based on positioning and container size
    const updateVideoStyle = () => {
      if (!positioning || !viewPortSize.width || !viewPortSize.height) {
        setVideoStyle({
          position: 'absolute',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        });
      }

      // The positioning data tells us which part of the video to show
      // We need to scale the video so that the region of interest fills the container

      // Calculate how much we need to scale the video
      const scaleWidth = viewPortSize.width / positioning.width;
      const scaleHeight = viewPortSize.height / positioning.height;

      // Use the same scale for both dimensions to maintain aspect ratio
      // We use the smaller scale to ensure the entire region fits in the viewport
      const scale = Math.min(scaleWidth, scaleHeight);

      // Calculate the dimensions of the entire video after scaling
      const videoWidth = 2560 * scale; // Assuming source video is 1920x1080
      const videoHeight = 1440 * scale;

      // Calculate the negative offset to position the region of interest
      const left = -(positioning.left * scale);
      const top = -(positioning.top * scale);

      // Update maxVideoSize
      setMaxVideoSize({
        width: positioning.width * scaleWidth,
        height: positioning.height * scaleHeight,
      });
      /*
      console.log(`Video styling for ${SessionTrack?.id || 'viewport'}:`, {
        scaleWidth,
        scaleHeight,
        scale,
        left,
        top,
        videoWidth,
        videoHeight,
        positioning,
        viewPortSize,
      });
*/
      setVideoStyle({
        position: 'absolute',
        width: `${videoWidth}px`,
        height: `${videoHeight}px`,
        left: `${left}px`,
        top: `${top}px`,
      });
    };

    // Get the container style with aspect ratio preservation
    useEffect(() => {
      updateContainerDimensions();
    }, [positioning]);

    const updateContainerDimensions = () => {
      const style = {
        overflow: 'hidden',
        position: 'relative',
        margin: '0 auto',
      };
      //console.log('Parent size:', parentSize);
      //console.log('Positioning:', positioning);
      let containerDimensions = {};
      if (parentSize.width <= parentSize.height) {
        const adjustmentFactor = parentSize.width / positioning.width;

        containerDimensions = {
          width: positioning.width * adjustmentFactor,
          height: positioning.height * adjustmentFactor,
        };

        setResizeBasedOn('width');
      } else {
        const adjustmentFactor = parentSize.height / positioning.height;

        containerDimensions = {
          width: positioning.width * adjustmentFactor,
          height: positioning.height * adjustmentFactor,
        };

        setResizeBasedOn('height');
      }

      setViewPortSize(containerDimensions);
      setContainerStyle(style);
    };
    useEffect(() => {
      if (!videoContainerSize || !controlsContainerSize) return;
      const sidebarTrackCount = Object.keys(tracks).length - 1;
      if (isFocusedTrack) {
        const videoHeight =
          videoContainerSize.height - controlsContainerSize.height;
        const gapAdjustment = (sidebarTrackCount - 1) * 10;
        setVideoHeight(videoHeight + gapAdjustment);
      } else {
        const videoHeight =
          (videoContainerSize.height - controlsContainerSize.height) /
          sidebarTrackCount;
        //const gapAdjustment = (sidebarTrackCount - 1) * 5;
        setVideoHeight(videoHeight);
      }
    }, [videoContainerSize, controlsContainerSize]);

    return (
      <Flex as="div" direction="column">
        <Flex.Item>
          <View
            as="div"
            margin="auto"
            width={width}
            height={height}
            background="primary-inverse"
            withVisualDebug={false}
            borderRadius="medium"
            shadow="above"
            elementRef={(el) => {
              containerRef.current = el;
            }}
            style={{
              overflow: 'hidden',
              contain: 'strict',
              transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
              transform: 'translateY(0) scale(1)',
              boxShadow:
                '0 6px 16px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.15)',
              border: '2px solid rgba(0, 0, 0, 0.1)',
              position: 'relative',
              zIndex: 1,
              '&:hover': {
                transform: 'translateY(-5px) scale(1.01)',
                boxShadow:
                  '0 12px 24px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.22)',
                borderColor: 'rgba(0, 100, 200, 0.3)',
              },
              '&:after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%)',
                pointerEvents: 'none',
                borderRadius: 'inherit',
              },
            }}
          >
            {positioning ? (
              <HlsViewer
                ref={hlsViewerRef}
                background="primary-inverse"
                hlsUrl={hlsUrl}
                regionBounds={positioning}
                showDebug={false}
                width={width}
                startTime={startTime}
                endTime={endTime}
                autoplay={autoplay}
                margin="auto"
                onTimeUpdate={(event) => {
                  // Extract the actual time from the video element
                  const currentTime = hlsViewerRef.current?.currentTime || 0;

                  // Update the fragment store with the current time
                  VideoFragmentStore.updateTime(fragmentId, currentTime);

                  // Pass the time to the callback if provided
                  if (onTimeUpdate) onTimeUpdate(currentTime);
                }}
                onPlay={() => {
                  if (onPlay) {
                    // Don't call VideoFragmentStore.play here, as it's already called by the play button
                    // This prevents the double call
                    onPlay();
                  }
                }}
                onPause={() => {
                  if (onPause) {
                    onPause();
                  }
                }}
                onEnded={() => {
                  if (onEnded) {
                    onEnded();
                  }
                }}
                onSeeking={() => {
                  if (onSeeking) {
                    onSeeking();
                  }
                }}
                onSeeked={() => {
                  if (onSeeked) {
                    onSeeked();
                  }
                }}
                onLoadStart={() => {
                  if (onLoadStart) {
                    onLoadStart();
                  }
                }}
                onLoadedMetadata={() => {
                  if (onLoadedMetadata) {
                    onLoadedMetadata();
                  }
                }}
                onLoadedData={() => {
                  if (onLoadedData) {
                    onLoadedData();
                  }
                }}
                onResize={() => {
                  if (onResize) {
                    onResize();
                  }
                }}
                onError={(error) => {
                  if (onError) {
                    onError(error);
                  }
                }}
                onReady={handleOnReady}
              />
            ) : (
              'No Positioning'
            )}
          </View>
        </Flex.Item>
        {displayProgressBar && (
          <Flex.Item>
            <View
              as="div"
              className="video-fragment-progress-bar-container"
              padding="small none"
              margin="none small"
            >
              <ProgressBar
                fragmentId={fragmentId}
                progressPercentage={progressPercentage}
                duration={endTime}
                currentTime={currentTime}
                videoRef={hlsViewerRef}
                onSeek={() => console.log('Seeked')}
                //handleSeek={handleSeek}
              />
            </View>
          </Flex.Item>
        )}
      </Flex>
    );
  }
);

HlsFragment.propTypes = {
  SessionTrack: PropTypes.object.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  style: PropTypes.object,
  displayProgressBar: PropTypes.bool,
  autoplay: PropTypes.bool,
  startTime: PropTypes.number,
  endTime: PropTypes.number,
  onTimeUpdate: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onEnded: PropTypes.func,
  onSeeking: PropTypes.func,
  onSeeked: PropTypes.func,
  onLoadStart: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  onLoadedData: PropTypes.func,
  onResize: PropTypes.func,
  onError: PropTypes.func,
  onReady: PropTypes.func,
};

export default HlsFragment;
